"use client"

import * as React from "react"
import * as PopoverPrimitive from "@radix-ui/react-popover"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"

function Popover({
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Root>) {
  return <PopoverPrimitive.Root data-slot="popover" {...props} />
}

function PopoverTrigger({
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Trigger>) {
  return <PopoverPrimitive.Trigger data-slot="popover-trigger" {...props} />
}

function PopoverContent({
  className,
  align = "center",
  sideOffset = 4,
  // Radix defaults collisionPadding to 0, which lets the panel sit flush against
  // the viewport edge — that is what reads as cramped, not the arrow. 16px
  // reserves a gutter for the shift to happen inside.
  collisionPadding = 16,
  // Keeps the arrow off the panel's rounded corners. Corner radius (--ui-radius,
  // currently 0) plus half the arrow's rendered width (28.28 / 2 ≈ 14). Past this
  // the clamp stops the arrow tracking the trigger, which is the correct
  // degradation for a trigger closer to the screen edge than the arrow is wide.
  arrowPadding = 16,
  children,
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Content>) {
  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        data-slot="popover-content"
        align={align}
        sideOffset={sideOffset}
        collisionPadding={collisionPadding}
        arrowPadding={arrowPadding}
        className={cn(
          // Surface shared with Tooltip (Figma popover node 455:2483): light
          // `surface-default` fill, 1px `border-color-default`, `shadow/sm`
          // elevation via the shape-token system, `text-color-primary` text.
          // What distinguishes it from the tooltip is chrome, not colour — the
          // arrow below and the close affordance.
          //
          // Width: `w-72` (288px) against Figma's 294px, a deliberate 6px
          // give to stay on Tailwind's scale. `max-w-[calc(100vw-2rem)]` is the
          // same viewport gutter TooltipContent uses, so the two overlays stop at
          // an identical margin instead of one pressing into collisionPadding.
          "bg-secondary text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 w-72 max-w-[calc(100vw-2rem)] origin-(--radix-popover-content-transform-origin) rounded-ui border p-4 shadow-ui outline-hidden",
          className
        )}
        {...props}
      >
        {children}
        {/* Figma draws the arrow as a 20x20 square rotated 45deg (hence the
            28.284px = 20 * sqrt(2) bounding box on `Frame 2609512`), straddling
            the panel edge — not as an SVG triangle. Reproduced the same way, so
            it inherits the panel's fill and border rather than needing a
            hand-stroked path.

            Only the two outward-facing edges are bordered. Radix rotates the
            arrow wrapper per side, so `border-b border-r` stays correct in the
            arrow's own coordinate space for all four sides. VERIFY VISUALLY on
            each side — see task 3.10. */}
        <PopoverPrimitive.Arrow
          asChild
          width={28}
          height={14}
          data-slot="popover-arrow"
        >
          <span className="bg-secondary border-b border-r block size-[20px] translate-y-[calc(-50%_-_1px)] rotate-45" />
        </PopoverPrimitive.Arrow>
      </PopoverPrimitive.Content>
    </PopoverPrimitive.Portal>
  )
}

/**
 * Close affordance for a popover. Figma places a 32x32 icon button at the
 * trailing edge of the popover header (`455:2483` → `Icon Button`, 32px box with
 * a 24px glyph), matching `button.tsx`'s `size="icon-sm"` step.
 *
 * Exported as its own part rather than baked into `PopoverContent` so the header
 * row stays composable — the header's title, spacing, and optional divider are
 * content decisions, not primitive ones.
 */
function PopoverClose({
  className,
  children,
  "aria-label": ariaLabel = "Close",
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Close>) {
  return (
    <PopoverPrimitive.Close
      data-slot="popover-close"
      aria-label={ariaLabel}
      className={cn(
        "text-popover-foreground focus-visible:border-ring focus-visible:ring-ring/50 inline-flex size-8 shrink-0 items-center justify-center rounded-ui outline-none focus-visible:ring-[3px] disabled:pointer-events-none",
        className
      )}
      {...props}
    >
      {children ?? <X className="size-6" aria-hidden="true" />}
    </PopoverPrimitive.Close>
  )
}

function PopoverAnchor({
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Anchor>) {
  return <PopoverPrimitive.Anchor data-slot="popover-anchor" {...props} />
}

export {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverClose,
  PopoverAnchor,
}
